import {
	CancelledError,
	type QueryClient,
	type QueryExecuteOptions,
	type QueryKey,
	queryOptions,
} from "@tanstack/react-query"
import { HttpError, isAbortError, isNetworkError } from "@/shared/api/errors"
import { getSessionVersion, setTokens } from "@/shared/lib/auth"
import { checkUser, getUserById } from "../api/client"
import { userKeys } from "../model/keys"
import { markUserConfirmed } from "./confirmed-at"

// The check keeps only the account id: the user itself goes to `byId`, the
// one place every reader takes it from.
async function runPlatformCheck(
	queryClient: QueryClient,
	params: Parameters<typeof checkUser>[0],
	version: number,
): Promise<number | null> {
	const result = await checkUser(params)
	if (!result.exists) return null
	await setTokens(result.access_token, result.refresh_token, version)
	queryClient.setQueryData(
		userQueryOptions(result.user.id).queryKey,
		result.user,
	)
	markUserConfirmed(result.user, version)
	return result.user.id
}

export function telegramCheckQueryOptions(
	queryClient: QueryClient,
	telegramUserId: string,
	version: number,
) {
	return queryOptions({
		queryKey: userKeys.check(telegramUserId),
		queryFn: () =>
			runPlatformCheck(queryClient, { tg_user_id: telegramUserId }, version),
		staleTime: 60_000,
	})
}

export function nativeCheckQueryOptions(
	queryClient: QueryClient,
	userId: number,
	deviceId: string,
	version: number,
) {
	return queryOptions({
		queryKey: userKeys.check(String(userId)),
		queryFn: () =>
			runPlatformCheck(
				queryClient,
				{ user_id: userId, device_id: deviceId },
				version,
			),
		staleTime: 60_000,
	})
}

export const USER_STALE_MS = 60_000

export function userQueryOptions(userId: number) {
	return queryOptions({
		queryKey: userKeys.byId(userId),
		queryFn: async () => {
			const version = getSessionVersion()
			const user = await getUserById(userId)
			markUserConfirmed(user, version)
			return user
		},
		staleTime: USER_STALE_MS,
	})
}

export function loadQuery<TData, TQueryKey extends QueryKey>(
	queryClient: QueryClient,
	query: QueryExecuteOptions<TData, Error, TData, TData, TQueryKey>,
	fresh: boolean,
): Promise<TData> {
	if (fresh) return queryClient.query({ ...query, staleTime: 0 })
	return queryClient.query({ ...query, staleTime: "static" })
}

export function isTransientFailure(error: unknown): boolean {
	return (
		error instanceof CancelledError ||
		isAbortError(error) ||
		isNetworkError(error) ||
		(error instanceof HttpError && error.status >= 500)
	)
}
